import { useCallback } from 'react'
import { addEdge } from '@xyflow/react'
import useFlowStore from '../../../components/chatflow/store'
import {
  TRIGGER_ID,
  EDGE_NORMAL, EDGE_OPTION, EDGE_FALLBACK, EDGE_UP_TO_DATE, EDGE_TRIGGER_STYLE,
  MARKER_NORMAL, MARKER_OPTION, MARKER_FALLBACK, MARKER_UP_TO_DATE, MARKER_TRIGGER,
} from '../constants'
import { buildEdges } from '../utils'
import { withoutTerminalLinks } from '../../../components/chatflow/actions'
import i18n from '@/i18n'

/**
 * resolveEdgeStyle — Resolves the style and marker of an edge from the source handle.
 * Used both in manual onConnect and in addConnectedNode (connect-on-drop).
 */
export function resolveEdgeStyle(sourceId: string, handleId: string | null | undefined) {
  if (handleId === 'fallback')          return { style: EDGE_FALLBACK,      markerEnd: MARKER_FALLBACK }
  if (handleId === 'up-to-date')        return { style: EDGE_UP_TO_DATE,    markerEnd: MARKER_UP_TO_DATE }
  if (handleId?.startsWith('opt-'))     return { style: EDGE_OPTION,        markerEnd: MARKER_OPTION }
  if (sourceId === TRIGGER_ID)          return { style: EDGE_TRIGGER_STYLE, markerEnd: MARKER_TRIGGER }
  return                                       { style: EDGE_NORMAL,         markerEnd: MARKER_NORMAL }
}

/**
 * useNodeManager — Manages all operations on nodes and edges:
 * add, delete, duplicate, update, connect, and rebuild edges.
 *
 * Receives nodes/setNodes/setEdges from React Flow (parent component state)
 * and rfInstanceRef to calculate positions on the canvas.
 */
interface UseNodeManagerParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nodes: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setNodes: (updater: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setEdges: (updater: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rfInstanceRef: React.MutableRefObject<any>;
}

export function useNodeManager({ nodes: _nodes, setNodes, setEdges, rfInstanceRef }: UseNodeManagerParams) { // eslint-disable-line @typescript-eslint/no-unused-vars
  const flow           = useFlowStore((s) => s.flow)
  const setFlow        = useFlowStore((s) => s.setFlow)
  const setSelectedNode = useFlowStore((s) => s.setSelectedNode)
  const setContextMenu  = useFlowStore((s) => s.setContextMenu)

  /* —— Rebuild edges from current node state —— */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rebuildEdges = useCallback((currentNodes: any[], startNodeId: string | null) => {
    const apiLike = currentNodes
      .filter((n) => n.type !== 'trigger')
      .map((n) => ({
        nodeId:        n.data.nodeId,
        nextNodeId:    n.data.nextNodeId,
        options:       n.data.options,
        fallbackNodeId: n.data.fallbackNodeId,
        firmwareUpToDateNodeId: n.data.firmwareUpToDateNodeId,
      }))
    setEdges(buildEdges(apiLike, startNodeId))
  }, [setEdges])

  /* —— Connection between nodes on the canvas —— */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onConnect = useCallback((params: any) => {
    const { source, sourceHandle, target } = params
    let nextStartNodeId = flow?.startNodeId

    if (source === TRIGGER_ID) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const alreadyConnected = (eds: any[]) => eds.some((e) => e.source === TRIGGER_ID)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setEdges((eds: any[]) => {
        if (alreadyConnected(eds)) return eds

        nextStartNodeId = target
        setFlow({ ...flow, startNodeId: target })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setNodes((nds: any[]) => nds.map((n) => ({ ...n, data: { ...n.data, isStart: n.id === target } })))

        const { style: edgeStyle, markerEnd: edgeMarker } = resolveEdgeStyle(source, sourceHandle)
        return addEdge({ ...params, type: 'smoothstep', style: edgeStyle, markerEnd: edgeMarker }, eds as never[])
      })
      void nextStartNodeId
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setNodes((nds: any[]) => nds.map((n) => {
      if (n.id !== source) return n
      if (sourceHandle?.startsWith('opt-')) {
        const optIdx = parseInt(sourceHandle.replace('opt-', ''), 10)
        const opts   = [...(n.data.options || [])]
        if (opts[optIdx]) opts[optIdx] = { ...opts[optIdx], nextNodeId: target }
        return { ...n, data: { ...n.data, options: opts } }
      }
      if (sourceHandle === 'fallback') {
        return { ...n, data: { ...n.data, fallbackNodeId: target } }
      }
      if (sourceHandle === 'up-to-date') {
        return { ...n, data: { ...n.data, firmwareUpToDateNodeId: target } }
      }
      return { ...n, data: { ...n.data, nextNodeId: target } }
    }))

    const { style: edgeStyle, markerEnd: edgeMarker } = resolveEdgeStyle(source, sourceHandle)
    void nextStartNodeId

    setEdges((eds: unknown) => addEdge({ ...params, type: 'smoothstep', style: edgeStyle, markerEnd: edgeMarker }, eds as never[]))
  }, [flow, setEdges, setNodes, setFlow])

  /* —— Node click: select in sidebar —— */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onNodeClick = useCallback((_: unknown, node: any) => {
    if (node.type === 'trigger') return
    setSelectedNode(node)
  }, [setSelectedNode])

  /* —— Canvas click: close sidebar and menu —— */
  const onPaneClick = useCallback(() => {
    setSelectedNode(null)
    setContextMenu(null)
  }, [setSelectedNode, setContextMenu])

  /* —— Update a node's data (from NodeSidebar) —— */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleNodeUpdate = useCallback((nodeId: string, updatedData: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setNodes((nds: any[]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updated = nds.map((n: any) => {
        if (n.id !== nodeId) return n
        // Picking a terminal action (e.g. transferToAgent) drops the node's
        // outgoing links, and rebuildEdges below removes their edges.
        const merged = withoutTerminalLinks({ ...n.data, ...updatedData, isStart: n.data.isStart })
        const nn     = { ...n, type: 'step', data: merged }
        // Supports renaming the nodeId
        if (updatedData.nodeId && updatedData.nodeId !== n.id) nn.id = updatedData.nodeId
        return nn
      })
      rebuildEdges(updated, flow?.startNodeId)
      return updated
    })
  }, [setNodes, rebuildEdges, flow])

  /* —— Remove a node from the canvas —— */
  const handleDeleteNode = useCallback((nodeId: string) => {
    if (nodeId === TRIGGER_ID) return
    if (flow?.startNodeId === nodeId) setFlow({ ...flow, startNodeId: null })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setNodes((nds: any[]) => nds.filter((n) => n.id !== nodeId))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setEdges((eds: any[]) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
    setSelectedNode(null)
    setContextMenu(null)
  }, [setNodes, setEdges, flow, setFlow, setSelectedNode, setContextMenu])

  /* —— Add a new node (centered on the current viewport) —— */
  const addNode = useCallback((type: string) => {
    const id     = `node_${crypto.randomUUID().slice(0, 8)}`
    const center = rfInstanceRef.current
      ? rfInstanceRef.current.screenToFlowPosition({
        x: window.innerWidth  / 2 - 120,
        y: window.innerHeight / 2,
      })
      : { x: 300, y: 400 }

    const defaultName = i18n.t('node.step_default_name')
    const newNode = {
      id, type,
      position: center,
      data: {
        nodeId:             id,
        name:               defaultName,
        messages:           [''],
        options:            [],
        action:             null,
        nextNodeId:             null,
        fallbackMessage:         null,
        fallbackNodeId:          null,
        firmwareUpToDateNodeId:  null,
        firmwareTargetType:      null,
        firmwareTimeoutMinutes:  null,
        fallbackMaxRetries:      null,
        isStart:                 false,
      },
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setNodes((nds: any[]) => [...nds, newNode])
    setSelectedNode(newNode)
    setContextMenu(null)
  }, [setNodes, setSelectedNode, setContextMenu, rfInstanceRef])

  /* —— Create a node at a specific position and connect to the source handle (connect-on-drop) —— */
  const addConnectedNode = useCallback((_type: string, flowPosition: { x: number; y: number }, fromNodeId: string, fromHandle: string) => {
    const id = `node_${crypto.randomUUID().slice(0, 8)}`
    const defaultName = i18n.t('node.step_default_name')

    const newNode = {
      id, type: 'step',
      position: flowPosition,
      data: {
        nodeId:             id,
        name:               defaultName,
        messages:           [''],
        options:            [],
        action:             null,
        nextNodeId:             null,
        fallbackMessage:         null,
        fallbackNodeId:          null,
        firmwareUpToDateNodeId:  null,
        firmwareTargetType:      null,
        firmwareTimeoutMinutes:  null,
        fallbackMaxRetries:      null,
        isStart:                 false,
      },
    }

    // Update the source node with the link to the new node
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setNodes((nds: any[]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updated = nds.map((n: any) => {
        if (n.id !== fromNodeId) return n
        if (n.type === 'trigger') return n // trigger is updated via setFlow below
        if (fromHandle?.startsWith('opt-')) {
          const optIdx = parseInt(fromHandle.replace('opt-', ''), 10)
          const opts   = [...(n.data.options || [])]
          if (opts[optIdx]) opts[optIdx] = { ...opts[optIdx], nextNodeId: id }
          return { ...n, data: { ...n.data, options: opts } }
        }
        if (fromHandle === 'up-to-date') {
          return { ...n, data: { ...n.data, firmwareUpToDateNodeId: id } }
        }
        return { ...n, data: { ...n.data, nextNodeId: id } }
      })
      return [...updated, newNode]
    })

    // If fromNode is the trigger, update startNodeId in flow meta
    if (fromNodeId === TRIGGER_ID) {
      setFlow({ ...flow, startNodeId: id })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setNodes((nds: any[]) => nds.map((n) => ({ ...n, data: { ...n.data, isStart: n.id === id } })))
    }

    // Create the edge with the correct style inherited from the source handle
    const { style, markerEnd } = resolveEdgeStyle(fromNodeId, fromHandle)
    const newEdge = {
      id:           `${fromNodeId}->${fromHandle}->${id}`,
      source:       fromNodeId,
      target:       id,
      sourceHandle: fromHandle,
      type:         'smoothstep',
      style,
      markerEnd,
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setEdges((eds: any[]) => [...eds, newEdge])

    // Open the sidebar with the new node selected
    setSelectedNode(newNode)
    setContextMenu(null)
  }, [setNodes, setEdges, setFlow, setSelectedNode, setContextMenu, flow])

  /* —— Duplicate a node (offset position) —— */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const duplicateNode = useCallback((node: any) => {
    if (!node || node.type === 'trigger') return
    const id  = `node_${crypto.randomUUID().slice(0, 8)}`
    const dup = {
      ...node,
      id,
      position: { x: node.position.x + 40, y: node.position.y + 60 },
      data:     { ...node.data, nodeId: id, name: `${node.data.name || ''} ${i18n.t('node.copy_suffix')}`, isStart: false, _mongoId: undefined },
      selected: false,
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setNodes((nds: any[]) => [...nds, dup])
    setContextMenu(null)
  }, [setNodes, setContextMenu])

  /* —— Prevent the trigger node from being deleted via keyboard —— */
  const onNodesDelete = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (deleted: any[]) => deleted.filter((n) => n.id !== TRIGGER_ID),
    [],
  )

  return {
    rebuildEdges,
    onConnect,
    onNodeClick,
    onPaneClick,
    handleNodeUpdate,
    handleDeleteNode,
    addNode,
    addConnectedNode,
    duplicateNode,
    onNodesDelete,
  }
}
